/**
 * Persists documentId → batchJobId mappings in localStorage so that
 * batch-aware timeout logic survives page refreshes.
 *
 * Entries auto-expire after TTL_MS (2 hours) to prevent unbounded growth.
 */

const STORAGE_KEY = 'synthesis-batch-job-map';
const TTL_MS = 2 * 60 * 60 * 1000;

interface MappingEntry {
  jobId: string;
  ts: number;
}

type MappingStore = Record<string, MappingEntry>;

function load(): MappingStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function save(map: MappingStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // localStorage full or unavailable — non-critical
  }
}

function pruneExpired(map: MappingStore): void {
  const now = Date.now();
  for (const id of Object.keys(map)) {
    if (map[id] && now - map[id].ts > TTL_MS) {
      delete map[id];
    }
  }
}

export function saveBatchJobMapping(documentId: string, jobId: string): void {
  const map = load();
  map[documentId] = { jobId, ts: Date.now() };
  pruneExpired(map);
  save(map);
}

export function saveBatchJobMappings(
  entries: Array<{ documentId: string; jobId: string }>
): void {
  const map = load();
  const now = Date.now();
  for (const { documentId, jobId } of entries) {
    map[documentId] = { jobId, ts: now };
  }
  pruneExpired(map);
  save(map);
}

export function removeBatchJobMappings(documentIds: string[]): void {
  if (documentIds.length === 0) return;
  const map = load();
  let changed = false;
  for (const documentId of documentIds) {
    if (map[documentId]) {
      delete map[documentId];
      changed = true;
    }
  }
  if (changed) save(map);
}

export function getBatchJobId(documentId: string): string | undefined {
  const map = load();
  const entry = map[documentId];
  if (!entry) return undefined;
  if (Date.now() - entry.ts > TTL_MS) return undefined;
  return entry.jobId;
}
